import {
  CONTENT_FORMAT_LABELS,
  PILLAR_LABELS,
  formatDate,
  type ContentItemDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Inbox, Sparkles, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import Markdown from 'react-markdown';
import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../components/Button.js';
import { MetaChip } from '../../components/StatusBadge.js';
import { api } from '../../lib/api.js';

/**
 * The pre-forslag review flow. Nova's agent-made ideas land hidden (PROPOSED);
 * this is where the area owner goes through them one by one — «legg til forslag»
 * (onto the board) or «forkast» (with a note we learn from). A dismissible,
 * Tinder-style wizard that opens on login when something is waiting, plus a
 * «N forslag venter»-badge to reopen it later.
 */
interface ProposalReviewContextValue {
  /** How many pre-forslag are waiting for this user. */
  count: number;
  /** Open the review wizard. */
  open: () => void;
}

const ProposalReviewContext = createContext<ProposalReviewContextValue | null>(null);

const DISMISS_KEY = 'nova-proposal-review-dismissed';

export function ProposalReviewProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpened = useRef(false);

  // Viewers never own areas, so they have nothing to review — skip the query.
  const canReview = Boolean(user) && user?.role !== 'VIEWER';

  const { data } = useQuery({
    queryKey: ['proposals', 'pending'],
    queryFn: () => api.get<{ items: ContentItemDto[]; total: number }>('/content/proposals/pending'),
    enabled: canReview,
    refetchInterval: 60_000,
  });

  const items = data?.items ?? [];
  const count = items.length;

  const open = useCallback(() => setIsOpen(true), []);

  // Auto-open once per session when something is waiting, unless the user has
  // already dismissed it this session. The nudge links here with ?forslag=1,
  // which always opens it.
  useEffect(() => {
    if (!canReview) return;
    const forced = searchParams.get('forslag') === '1';
    if (forced && count > 0) {
      setIsOpen(true);
      // Consume the param so a refresh does not keep forcing it open.
      searchParams.delete('forslag');
      setSearchParams(searchParams, { replace: true });
      autoOpened.current = true;
      return;
    }
    if (autoOpened.current || count === 0) return;
    const dismissed = safeSessionGet(DISMISS_KEY) === '1';
    if (!dismissed) {
      setIsOpen(true);
      autoOpened.current = true;
    }
  }, [canReview, count, searchParams, setSearchParams]);

  const close = useCallback(() => {
    setIsOpen(false);
    safeSessionSet(DISMISS_KEY, '1');
  }, []);

  const value = useMemo<ProposalReviewContextValue>(() => ({ count, open }), [count, open]);

  return (
    <ProposalReviewContext.Provider value={value}>
      {children}
      <ProposalReviewWizard open={isOpen} items={items} onClose={close} />
    </ProposalReviewContext.Provider>
  );
}

export function useProposalReview(): ProposalReviewContextValue {
  const context = useContext(ProposalReviewContext);
  if (!context) throw new Error('useProposalReview må brukes inne i ProposalReviewProvider.');
  return context;
}

/** The «N forslag venter»-badge for the top bar; hidden when nothing waits. */
export function ProposalReviewBadge() {
  const { count, open } = useProposalReview();
  if (count === 0) return null;
  return (
    <button
      type="button"
      onClick={open}
      className="border-violet/40 bg-violet/12 text-violet hover:bg-violet/20 inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition"
      title="Nova har forslag klare til gjennomgang"
    >
      <Sparkles size={12} aria-hidden />
      <span className="num-tabular">{count}</span>
      <span className="hidden sm:inline">forslag venter</span>
    </button>
  );
}

function ProposalReviewWizard({
  open,
  items,
  onClose,
}: {
  open: boolean;
  items: ContentItemDto[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [index, setIndex] = useState(0);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Keep a frozen snapshot of the total for the «x av N»-progress so it does not
  // jump as items are reviewed and the pending list shrinks under us.
  const totalRef = useRef(items.length);
  useEffect(() => {
    if (open) {
      totalRef.current = items.length;
      setIndex(0);
      setNote('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const current = items[index] ?? null;
  const reviewed = totalRef.current - items.length;

  const review = useMutation({
    mutationFn: async (vars: { id: string; action: 'accept' | 'discard'; text: string }) => {
      const path = `/content/${vars.id}/${vars.action}-proposal`;
      const payload =
        vars.action === 'accept'
          ? { comment: vars.text || undefined }
          : { reason: vars.text || undefined };
      return api.post(path, payload);
    },
    onSuccess: async () => {
      setNote('');
      setError(null);
      // The list shrinks server-side; refetch and let `current` fall to the next.
      await queryClient.invalidateQueries({ queryKey: ['proposals', 'pending'] });
      // Also refresh the board/plan so an accepted proposal appears there.
      await queryClient.invalidateQueries({ queryKey: ['content'] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Noe gikk galt. Prøv igjen.');
    },
  });

  const act = (action: 'accept' | 'discard') => {
    if (!current) return;
    review.mutate({ id: current.id, action, text: note.trim() });
  };

  return createPortalSafe(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Gå gjennom forslag fra Nova"
            className="menu-surface relative flex max-h-[90dvh] w-full max-w-lg flex-col overflow-hidden"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Header */}
            <div className="border-hairline flex items-start gap-3 border-b px-5 py-4">
              <div className="min-w-0 flex-1">
                <p className="label-micro text-violet flex items-center gap-1.5">
                  <Sparkles size={12} aria-hidden /> Forslag fra Nova
                </p>
                <h2 className="font-display text-ink mt-1 text-base">
                  {current ? 'Hva synes du om dette?' : 'Alt er gjennomgått'}
                </h2>
              </div>
              {current && totalRef.current > 0 && (
                <span className="num-tabular text-ink-soft shrink-0 text-xs">
                  {Math.min(reviewed + 1, totalRef.current)} av {totalRef.current}
                </span>
              )}
              <button
                type="button"
                onClick={onClose}
                aria-label="Lukk"
                className="text-ink-soft hover:bg-raised hover:text-ink -mr-1 -mt-1 shrink-0 rounded-lg p-2 transition"
              >
                <X size={18} aria-hidden />
              </button>
            </div>

            {current ? (
              <>
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                  <div className="mb-3 flex flex-wrap items-center gap-1.5">
                    {current.area && (
                      <MetaChip color={current.area.color}>{current.area.name}</MetaChip>
                    )}
                    <MetaChip>{PILLAR_LABELS[current.pillar]}</MetaChip>
                    <MetaChip>{CONTENT_FORMAT_LABELS[current.format]}</MetaChip>
                    {current.publishAt && (
                      <MetaChip>Foreslått {formatDate(current.publishAt)}</MetaChip>
                    )}
                  </div>

                  <h3 className="font-display text-ink text-lg leading-snug">{current.title}</h3>

                  {current.summary && (
                    <p className="text-ink-soft mt-2 text-sm leading-relaxed">{current.summary}</p>
                  )}

                  {current.agentRationale && (
                    <div className="border-hairline bg-base/40 mt-4 rounded-lg border p-3">
                      <p className="label-micro text-accent mb-1.5">Hvorfor Nova foreslår dette</p>
                      <div className="prose-nova text-ink-soft space-y-2 text-sm leading-relaxed">
                        <Markdown
                          components={{
                            p: ({ children }) => <p>{children}</p>,
                            strong: ({ children }) => (
                              <strong className="text-ink font-semibold">{children}</strong>
                            ),
                            a: ({ children, href }) => (
                              <a
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                className="text-accent underline"
                              >
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {current.agentRationale}
                        </Markdown>
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <label htmlFor="proposal-note" className="label-micro mb-1.5 block">
                      Kommentar (valgfritt)
                    </label>
                    <textarea
                      id="proposal-note"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={2}
                      placeholder="Legg til en kommentar — ros ved «Legg til», eller hva vi skal lære av ved «Forkast»."
                      className="border-hairline bg-base/60 text-ink focus:border-accent/50 w-full rounded-lg border px-3 py-2 text-sm transition"
                    />
                  </div>

                  {error && (
                    <p
                      role="alert"
                      className="border-danger/30 bg-danger/10 text-danger mt-3 rounded-lg border px-3 py-2 text-sm"
                    >
                      {error}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="border-hairline flex items-center gap-2 border-t px-5 py-4">
                  <button
                    type="button"
                    onClick={() => act('discard')}
                    disabled={review.isPending}
                    className="border-danger/30 bg-danger/12 text-danger hover:bg-danger/20 inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X size={16} aria-hidden /> Forkast
                  </button>
                  <button
                    type="button"
                    onClick={() => act('accept')}
                    disabled={review.isPending}
                    className="bg-success text-base hover:bg-success/90 shadow-glow-sm inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Check size={16} aria-hidden /> Legg til forslag
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
                <span className="bg-success/12 text-success flex h-12 w-12 items-center justify-center rounded-full">
                  <Inbox size={22} aria-hidden />
                </span>
                <p className="text-ink text-sm">
                  {totalRef.current > 0
                    ? `Du har gått gjennom ${totalRef.current} ${
                        totalRef.current === 1 ? 'forslag' : 'forslag'
                      }. Fint jobbet!`
                    : 'Ingen forslag venter akkurat nå.'}
                </p>
                <Button variant="primary" size="sm" onClick={onClose}>
                  Ferdig
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
  );
}

// ── Små hjelpere ─────────────────────────────────────────────────────────────

function safeSessionGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* storage blocked — the wizard still works, it just re-opens next load */
  }
}

/** Portals to the body, guarding against SSR/thumbnail contexts with no DOM. */
function createPortalSafe(node: React.ReactNode): React.ReactPortal | null {
  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
}
